import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Alert,
  Platform,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input, CurrencyPicker, RichTextEditor } from '../design-system';
import { useLocale } from '../../contexts/LocaleContext';
import { Button } from '../design-system';
import { ProductImageUpload, ProductImage } from './ProductImageUpload';
import { ProductVariantForm, ProductVariant } from './ProductVariantForm';
import { BarcodeScanner } from './BarcodeScanner';
import { UnitPicker } from './UnitPicker';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { ZoneMapPicker, ZoneResult } from '../ZoneMapPicker';
import { apiService } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';
import { businessCurrency } from '../../utils/businessDefaults';

type ProductType = 'physical' | 'digital' | 'service';

export interface AddonPricingZone {
  name: string;
  price: number;
  currency?: string;
  mode?: 'country' | 'city' | 'radius';
  cities?: string[];
  polygon?: [number, number][];
  polygons?: [number, number][][];
  center?: { latitude: number; longitude: number };
  radiusKm?: number;
}

export interface ProductAddon {
  id: string;
  name: string;
  price: number;
  description?: string;
  isRequired?: boolean;
  maxQuantity?: number;
  pricingZones?: AddonPricingZone[];
}

export interface ProductPricingZone {
  name: string;
  price: number;
  salePrice?: number;
  currency?: string;
  mode?: 'country' | 'city' | 'radius';
  cities?: string[];
  polygon?: [number, number][];
  polygons?: [number, number][][];
  center?: { latitude: number; longitude: number };
  radiusKm?: number;
}

export interface ProductFormData {
  productType: ProductType;
  name: string;
  description?: string;
  brand?: string;
  sku?: string;
  // Physical only
  barcode?: string;
  // Pricing
  price: number;
  currency: string;
  priceUnit?: string;       // unit name (label shown to customers)
  unitId?: string;          // reference to business Unit document
  salePrice?: number;
  costPrice?: number;
  // Inventory / capacity / slots
  inventoryTracking: boolean;
  inventoryQuantity?: number;
  lowStockThreshold?: number;
  /** Least quantity an order may contain (null clears it). */
  minimumOrderQuantity?: number | null;
  // Status
  availability: 'in_stock' | 'out_of_stock' | 'preorder' | 'backorder';
  // Physical only
  condition: 'new' | 'refurbished' | 'used';
  weight?: number;
  dimensions?: { length?: number; width?: number; height?: number };
  // Digital only
  digitalDelivery?: 'link' | 'email' | 'auto';
  // Service only
  serviceDelivery?: 'online' | 'in_person' | 'both';
  serviceLocation?: string;
  // Catalogue membership — many-to-many, a product may belong to 0..N catalogues
  catalogueIds: string[];
  // Media & variants
  images: ProductImage[];
  categoryIds: string[];
  hasVariants: boolean;
  variants?: ProductVariant[];
  // Addons
  addons?: ProductAddon[];
  // Location-based pricing
  pricingZones?: ProductPricingZone[];
}

export interface ProductFormProps {
  initialData?: Partial<ProductFormData>;
  catalogueId: string;
  onSubmit: (data: ProductFormData) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  containerStyle?: any;
}

// ─── Type selector ─────────────────────────────────────────────────────────────

const TYPE_OPTIONS: {
  value: ProductType;
  label: string;
  icon: string;
  description: string;
}[] = [
  { value: 'physical',  label: 'Physical',  icon: 'cube-outline',        description: 'Shipped / delivered item' },
  { value: 'digital',   label: 'Digital',   icon: 'sparkles-outline',    description: 'Subscription, license, design…' },
  { value: 'service',   label: 'Service',   icon: 'briefcase-outline',   description: 'Session, consultation, task…' },
];

const TypeChip: React.FC<{
  option: typeof TYPE_OPTIONS[number];
  selected: boolean;
  onPress: () => void;
}> = ({ option, selected, onPress }) => {
  const styles = useThemedStyles(getStyles);
  return (
  <TouchableOpacity
    style={[styles.typeChip, selected && styles.typeChipActive]}
    onPress={onPress}
    activeOpacity={0.7}
  >
    <Ionicons name={option.icon as any} size={20} color={selected ? colors.white : colors.primary} />
    <View style={styles.typeChipText}>
      <Text style={[styles.typeChipLabel, selected && styles.typeChipLabelActive]}>{option.label}</Text>
      <Text style={[styles.typeChipDesc, selected && styles.typeChipDescActive]}>{option.description}</Text>
    </View>
  </TouchableOpacity>
);
};

// ─── Chip row (shared between delivery method selectors) ──────────────────────

function ChipRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T | undefined;
  onChange: (v: T) => void;
}) {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.selectRow}>
      <Text style={styles.selectLabel}>{label}</Text>
      <View style={styles.selectButtons}>
        {options.map((o) => (
          <TouchableOpacity
            key={o.value}
            style={[styles.chip, value === o.value && styles.chipActive]}
            onPress={() => onChange(o.value)}
            activeOpacity={0.7}
          >
            <Text style={[styles.chipText, value === o.value && styles.chipTextActive]}>{o.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

// ─── Main form ─────────────────────────────────────────────────────────────────

export const ProductForm: React.FC<ProductFormProps> = ({
  initialData,
  catalogueId,
  productId,
  onSubmit,
  onCancel,
  submitLabel = 'Save Product',
  loading = false,
  containerStyle,
}: any) => {
  const { t } = useLocale();
  const styles = useThemedStyles(getStyles);
  const [formData, setFormData] = useState<ProductFormData>({
    catalogueIds: catalogueId && catalogueId !== 'default' ? [catalogueId] : [],
    productType: 'physical',
    name: '',
    description: '',
    brand: '',
    sku: '',
    barcode: '',
    price: 0,
    currency: businessCurrency(),
    priceUnit: '',
    unitId: '',
    salePrice: undefined,
    costPrice: undefined,
    inventoryTracking: false,
    inventoryQuantity: undefined,
    lowStockThreshold: undefined,
    availability: 'in_stock',
    condition: 'new',
    weight: undefined,
    dimensions: undefined,
    digitalDelivery: undefined,
    serviceDelivery: undefined,
    serviceLocation: '',
    images: [],
    categoryIds: [],
    hasVariants: false,
    variants: [],
    addons: [],
    pricingZones: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showZonePicker, setShowZonePicker] = useState(false);
  const [editingZoneIndex, setEditingZoneIndex] = useState<number | null>(null);
  // Addon zone picker state: which addon (by index) and which zone within it
  const [addonZonePicker, setAddonZonePicker] = useState<{ addonIndex: number; zoneIndex: number | null; mode: 'country' | 'city' | 'radius' } | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        images: initialData.images || [],
        variants: initialData.variants || [],
        categoryIds: initialData.categoryIds || [],
        catalogueIds: initialData.catalogueIds || [],
        addons: initialData.addons || [],
        pricingZones: initialData.pricingZones || [],
      }));
    }
  }, [initialData]);

  // Business's catalogues, for the multi-select membership picker below.
  const [catalogues, setCatalogues] = useState<{ _id: string; name: string }[]>([]);
  useEffect(() => {
    apiService.getCatalogues()
      .then((res) => {
        if (res.statusCode === 1000 && res.data) {
          setCatalogues((res.data.catalogues || []).map((c: any) => ({ _id: c._id, name: c.name })));
        }
      })
      .catch((err) => console.error('[ProductForm] Failed to load catalogues:', err));
  }, []);

  const toggleCatalogue = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      catalogueIds: prev.catalogueIds.includes(id)
        ? prev.catalogueIds.filter((c) => c !== id)
        : [...prev.catalogueIds, id],
    }));
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!formData.name.trim()) e.name = 'Product name is required';
    if (formData.price <= 0) e.price = 'Price must be greater than 0';
    if (formData.salePrice !== undefined && formData.salePrice >= formData.price) {
      e.salePrice = 'Sale price must be less than regular price';
    }
    if (formData.inventoryTracking && formData.inventoryQuantity !== undefined && formData.inventoryQuantity < 0) {
      e.inventoryQuantity = 'Cannot be negative';
    }
    if (formData.hasVariants && (!formData.variants || formData.variants.length === 0)) {
      e.variants = 'At least one variant is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) { Alert.alert('Validation Error', 'Please fix the errors in the form'); return; }
    try { await onSubmit(formData); } catch (err: any) { Alert.alert('Error', err.message || 'Failed to save product'); }
  };

  const updateField = <K extends keyof ProductFormData>(field: K, value: ProductFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const e = { ...prev }; delete e[field]; return e; });
  };

  const isPhysical = formData.productType === 'physical';
  const isDigital  = formData.productType === 'digital';
  const isService  = formData.productType === 'service';

  // Labels that change per type
  const inventoryLabel      = isPhysical ? 'Inventory'       : isDigital ? 'Access / Seats' : 'Booking Slots';
  const inventoryQtyLabel   = isPhysical ? 'Quantity in Stock' : isDigital ? 'Available seats / licenses' : 'Available slots';
  const inventoryTrackLabel = isPhysical ? 'Track inventory' : isDigital ? 'Limit access slots' : 'Limit booking slots';

  return (
    <>
    <ScrollView style={[styles.container, containerStyle]} showsVerticalScrollIndicator={false}>

      {/* ── Product Type ─────────────────────────────────────────────────── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Product Type</Text>
        <View style={styles.typeRow}>
          {TYPE_OPTIONS.map((opt) => (
            <TypeChip
              key={opt.value}
              option={opt}
              selected={formData.productType === opt.value}
              onPress={() => updateField('productType', opt.value)}
            />
          ))}
        </View>
      </View>

      {/* ── Catalogues ───────────────────────────────────────────────────── */}
      {catalogues.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Catalogues</Text>
          <Text style={styles.selectLabel}>A product may belong to any number of catalogues (or none).</Text>
          <View style={styles.selectButtons}>
            {catalogues.map((cat) => {
              const selected = formData.catalogueIds.includes(cat._id);
              return (
                <TouchableOpacity
                  key={cat._id}
                  style={[styles.chip, selected && styles.chipActive]}
                  onPress={() => toggleCatalogue(cat._id)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.chipText, selected && styles.chipTextActive]}>{cat.name}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      )}

      {/* ── Basic Info ───────────────────────────────────────────────────── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Basic Information</Text>

        <Input
          label="Product Name *"
          value={formData.name}
          onChangeText={(t) => updateField('name', t)}
          placeholder="Enter product name"
          error={errors.name}
          required
        />

        <RichTextEditor
          label="Description"
          value={formData.description || ''}
          onChange={(t) => updateField('description', t)}
          placeholder="Describe your product…"
          maxLength={5000}
          showCharCount
          mode="product"
        />

        <Input
          label="Brand"
          value={formData.brand || ''}
          onChangeText={(t) => updateField('brand', t)}
          placeholder="Enter brand name"
        />

        <View style={styles.row}>
          <View style={styles.halfWidth}>
            <Input
              label="SKU"
              value={formData.sku || ''}
              onChangeText={(t) => updateField('sku', t)}
              placeholder="SKU-001"
            />
          </View>

          {/* Barcode — physical only */}
          {isPhysical && (
            <View style={styles.halfWidth}>
              <Text style={styles.inputLabel}>Barcode</Text>
              <View style={styles.barcodeRow}>
                <View style={styles.flex1}>
                  <Input
                    value={formData.barcode || ''}
                    onChangeText={(t) => updateField('barcode', t)}
                    placeholder="Scan or type"
                  />
                </View>
                <TouchableOpacity
                  style={styles.scanButton}
                  onPress={() => setShowBarcodeScanner(true)}
                  activeOpacity={0.7}
                >
                  <Ionicons name="barcode-outline" size={22} color={colors.white} />
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* ── Pricing ──────────────────────────────────────────────────────── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Pricing</Text>

        <View style={styles.row}>
          <View style={styles.halfWidth}>
            <Input
              label="Price *"
              value={formData.price.toString()}
              onChangeText={(t) => { const n = parseFloat(t); updateField('price', isNaN(n) ? 0 : n); }}
              placeholder="0.00"
              keyboardType="numeric"
              error={errors.price}
              required
            />
          </View>
          <View style={styles.halfWidth}>
            <CurrencyPicker
              label="Currency"
              value={formData.currency}
              onChange={(code) => updateField('currency', code)}
            />
          </View>
        </View>

        {/* Unit picker — all product types */}
        <View>
          <Text style={styles.fieldLabel}>Unit</Text>
          <UnitPicker
            value={formData.priceUnit || undefined}
            unitId={formData.unitId || undefined}
            onSelect={(unit) => {
              updateField('priceUnit', unit?.name || '');
              updateField('unitId', unit?._id || '');
            }}
            placeholder={isPhysical ? 'e.g. Item, Box, Kg, Liter' : isDigital ? 'e.g. License, Seat, Month' : 'e.g. Session, Hour, Visit'}
          />
          <Text style={styles.fieldHelper}>How this product is measured or sold</Text>
        </View>

        <View style={styles.row}>
          <View style={styles.halfWidth}>
            <Input
              label="Sale Price"
              value={formData.salePrice?.toString() || ''}
              onChangeText={(t) => { const n = parseFloat(t); updateField('salePrice', t ? (isNaN(n) ? undefined : n) : undefined); }}
              placeholder="0.00"
              keyboardType="numeric"
              error={errors.salePrice}
            />
          </View>
          <View style={styles.halfWidth}>
            <Input
              label="Cost Price"
              value={formData.costPrice?.toString() || ''}
              onChangeText={(t) => { const n = parseFloat(t); updateField('costPrice', t ? (isNaN(n) ? undefined : n) : undefined); }}
              placeholder="0.00"
              keyboardType="numeric"
            />
          </View>
        </View>
      </View>

      {/* ── Inventory / Capacity ─────────────────────────────────────────── */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{inventoryLabel}</Text>

        <View style={styles.toggleRow}>
          <Text style={styles.toggleLabel}>{inventoryTrackLabel}</Text>
          <Button
            title={formData.inventoryTracking ? 'On' : 'Off'}
            onPress={() => updateField('inventoryTracking', !formData.inventoryTracking)}
            variant={formData.inventoryTracking ? 'primary' : 'outline'}
            size="sm"
          />
        </View>

        {formData.inventoryTracking && (
          <>
            <Input
              label={inventoryQtyLabel}
              value={formData.inventoryQuantity?.toString() || ''}
              onChangeText={(t) => { const n = parseInt(t); updateField('inventoryQuantity', t ? (isNaN(n) ? undefined : n) : undefined); }}
              placeholder="0"
              keyboardType="numeric"
              error={errors.inventoryQuantity}
            />
            {isPhysical && (
              <Input
                label="Low Stock Threshold"
                value={formData.lowStockThreshold?.toString() || ''}
                onChangeText={(t) => { const n = parseInt(t); updateField('lowStockThreshold', t ? (isNaN(n) ? undefined : n) : undefined); }}
                placeholder="5"
                keyboardType="numeric"
              />
            )}
          </>
        )}

        {/* Minimum order quantity — enforced on every order (storefront,
            manual orders, API). Blank = no minimum. */}
        <Input
          label={t('products.minimumOrderQuantity.label')}
          value={formData.minimumOrderQuantity ? String(formData.minimumOrderQuantity) : ''}
          onChangeText={(v) => {
            const n = parseInt(v.replace(/[^0-9]/g, ''), 10);
            updateField('minimumOrderQuantity', v.trim() && !isNaN(n) && n > 1 ? n : null);
          }}
          placeholder={t('products.minimumOrderQuantity.placeholder')}
          keyboardType="number-pad"
          helperText={t('products.minimumOrderQuantity.hint')}
        />

        {/* Availability — simplified for digital/service */}
        {isPhysical ? (
          <ChipRow
            label="Availability"
            options={[
              { value: 'in_stock',    label: 'In Stock' },
              { value: 'out_of_stock', label: 'Out of Stock' },
              { value: 'preorder',    label: 'Pre-order' },
              { value: 'backorder',   label: 'Backorder' },
            ]}
            value={formData.availability}
            onChange={(v) => updateField('availability', v)}
          />
        ) : (
          <ChipRow
            label="Availability"
            options={[
              { value: 'in_stock',     label: 'Available' },
              { value: 'out_of_stock', label: 'Unavailable' },
            ]}
            value={formData.availability === 'out_of_stock' ? 'out_of_stock' : 'in_stock'}
            onChange={(v) => updateField('availability', v)}
          />
        )}
      </View>

      {/* ── Physical: Condition ───────────────────────────────────────────── */}
      {isPhysical && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Condition</Text>
          <ChipRow
            label="Item Condition"
            options={[
              { value: 'new',         label: 'New' },
              { value: 'refurbished', label: 'Refurbished' },
              { value: 'used',        label: 'Used' },
            ]}
            value={formData.condition}
            onChange={(v) => updateField('condition', v)}
          />
        </View>
      )}

      {/* ── Physical: Shipping ────────────────────────────────────────────── */}
      {isPhysical && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Shipping</Text>
          <Input
            label="Weight (grams)"
            value={formData.weight?.toString() || ''}
            onChangeText={(t) => { const n = parseFloat(t); updateField('weight', t ? (isNaN(n) ? undefined : n) : undefined); }}
            placeholder="0"
            keyboardType="numeric"
          />
          <Text style={styles.selectLabel}>Dimensions (cm)</Text>
          <View style={styles.row}>
            {(['length', 'width', 'height'] as const).map((dim) => (
              <View key={dim} style={styles.thirdWidth}>
                <Input
                  label={dim.charAt(0).toUpperCase() + dim.slice(1)}
                  value={formData.dimensions?.[dim]?.toString() || ''}
                  onChangeText={(t) => {
                    const n = parseFloat(t);
                    updateField('dimensions', { ...formData.dimensions, [dim]: t ? (isNaN(n) ? undefined : n) : undefined });
                  }}
                  placeholder="0"
                  keyboardType="numeric"
                />
              </View>
            ))}
          </View>
        </View>
      )}

      {/* ── Digital: Delivery ─────────────────────────────────────────────── */}
      {isDigital && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Delivery</Text>
          <ChipRow
            label="How will access be delivered?"
            options={[
              { value: 'link',  label: 'Download / Link' },
              { value: 'email', label: 'Via Email' },
              { value: 'auto',  label: 'Automatic' },
            ]}
            value={formData.digitalDelivery}
            onChange={(v) => updateField('digitalDelivery', v)}
          />
        </View>
      )}

      {/* ── Service: Delivery & Location ──────────────────────────────────── */}
      {isService && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Delivery</Text>
          <ChipRow
            label="How is the service delivered?"
            options={[
              { value: 'online',    label: 'Online' },
              { value: 'in_person', label: 'In Person' },
              { value: 'both',      label: 'Both' },
            ]}
            value={formData.serviceDelivery}
            onChange={(v) => updateField('serviceDelivery', v)}
          />
          <Input
            label={formData.serviceDelivery === 'online' ? 'Meeting Link' : 'Location / Address'}
            value={formData.serviceLocation || ''}
            onChangeText={(t) => updateField('serviceLocation', t)}
            placeholder={formData.serviceDelivery === 'online' ? 'https://meet.google.com/…' : 'Address or city'}
          />
        </View>
      )}

      {/* ── Images ───────────────────────────────────────────────────────── */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Images</Text>
          <Text style={styles.optionalLabel}>(Optional)</Text>
        </View>
        <Text style={styles.sectionDescription}>
          If no images are uploaded, the product will display with name initials.
        </Text>
        <ProductImageUpload
          images={formData.images}
          onChange={(imgs) => updateField('images', imgs)}
          productId={productId}
        />
      </View>

      {/* ── Variants — physical & digital only ───────────────────────────── */}
      {!isService && (
        <View style={styles.section}>
          <View style={styles.toggleRow}>
            <Text style={styles.toggleLabel}>Has Variants</Text>
            <Button
              title={formData.hasVariants ? 'Yes' : 'No'}
              onPress={() => updateField('hasVariants', !formData.hasVariants)}
              variant={formData.hasVariants ? 'primary' : 'outline'}
              size="sm"
            />
          </View>
          {formData.hasVariants && (
            <>
              {errors.variants && <Text style={styles.errorText}>{errors.variants}</Text>}
              <ProductVariantForm
                variants={formData.variants || []}
                onChange={(v) => updateField('variants', v)}
                basePrice={formData.price}
                currency={formData.currency}
              />
            </>
          )}
        </View>
      )}

      {/* ── Addons ───────────────────────────────────────────────────────── */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Add-ons</Text>
          <Text style={styles.optionalLabel}>(Optional)</Text>
        </View>
        <Text style={styles.sectionDescription}>
          Optional extras customers can add (e.g. gift wrap, extra toppings, extended warranty).
        </Text>
        {(formData.addons || []).map((addon, index) => (
          <View key={addon.id} style={styles.addonCard}>
            <View style={styles.addonHeader}>
              <Text style={styles.addonIndex}>Add-on {index + 1}</Text>
              <TouchableOpacity
                onPress={() => updateField('addons', (formData.addons || []).filter((_, i) => i !== index))}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close-circle" size={20} color={colors.error} />
              </TouchableOpacity>
            </View>
            <Input
              label="Name *"
              value={addon.name}
              onChangeText={(t) => {
                const updated = [...(formData.addons || [])];
                updated[index] = { ...updated[index], name: t };
                updateField('addons', updated);
              }}
              placeholder="e.g. Gift wrap"
            />
            <View style={styles.row}>
              <View style={styles.halfWidth}>
                <Input
                  label={`Price (${formData.currency})`}
                  value={addon.price.toString()}
                  onChangeText={(t) => {
                    const n = parseFloat(t);
                    const updated = [...(formData.addons || [])];
                    updated[index] = { ...updated[index], price: isNaN(n) ? 0 : n };
                    updateField('addons', updated);
                  }}
                  placeholder="0.00"
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.halfWidth}>
                <Input
                  label="Max Qty"
                  value={addon.maxQuantity?.toString() || ''}
                  onChangeText={(t) => {
                    const n = parseInt(t);
                    const updated = [...(formData.addons || [])];
                    updated[index] = { ...updated[index], maxQuantity: t ? (isNaN(n) ? undefined : n) : undefined };
                    updateField('addons', updated);
                  }}
                  placeholder="Unlimited"
                  keyboardType="numeric"
                />
              </View>
            </View>
            <Input
              label="Description"
              value={addon.description || ''}
              onChangeText={(t) => {
                const updated = [...(formData.addons || [])];
                updated[index] = { ...updated[index], description: t };
                updateField('addons', updated);
              }}
              placeholder="Optional detail"
            />
            <View style={styles.addonToggleRow}>
              <Text style={styles.addonToggleLabel}>Required</Text>
              <Button
                title={addon.isRequired ? 'Yes' : 'No'}
                onPress={() => {
                  const updated = [...(formData.addons || [])];
                  updated[index] = { ...updated[index], isRequired: !addon.isRequired };
                  updateField('addons', updated);
                }}
                variant={addon.isRequired ? 'primary' : 'outline'}
                size="sm"
              />
            </View>

            {/* Addon location pricing zones */}
            <View style={styles.addonZonesSection}>
              <Text style={styles.addonZonesLabel}>Location Pricing</Text>
              {(addon.pricingZones || []).map((zone, zi) => (
                <View key={zi} style={styles.addonZoneCard}>
                  <View style={styles.addonZoneCardHeader}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.addonZoneName}>{zone.name}</Text>
                      <Text style={styles.addonZoneMeta}>
                        {zone.cities?.length ? zone.cities.join(', ') : zone.radiusKm ? `${zone.radiusKm} km` : 'Polygon'}
                      </Text>
                    </View>
                    <TouchableOpacity
                      onPress={() => setAddonZonePicker({ addonIndex: index, zoneIndex: zi, mode: zone.mode || 'city' })}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      style={{ marginRight: spacing.xs }}
                    >
                      <Ionicons name="pencil-outline" size={16} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      onPress={() => {
                        const updated = [...(formData.addons || [])];
                        updated[index] = { ...updated[index], pricingZones: (updated[index].pricingZones || []).filter((_, i) => i !== zi) };
                        updateField('addons', updated);
                      }}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="close-circle" size={18} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.row}>
                    <View style={styles.halfWidth}>
                      <Input
                        label="Price *"
                        value={zone.price.toString()}
                        onChangeText={(t) => {
                          const n = parseFloat(t);
                          const updated = [...(formData.addons || [])];
                          const zones = [...(updated[index].pricingZones || [])];
                          zones[zi] = { ...zones[zi], price: isNaN(n) ? 0 : n };
                          updated[index] = { ...updated[index], pricingZones: zones };
                          updateField('addons', updated);
                        }}
                        placeholder="0.00"
                        keyboardType="decimal-pad"
                      />
                    </View>
                    <View style={styles.halfWidth}>
                      <CurrencyPicker
                        label="Currency"
                        value={zone.currency || formData.currency}
                        onChange={(code) => {
                          const updated = [...(formData.addons || [])];
                          const zones = [...(updated[index].pricingZones || [])];
                          zones[zi] = { ...zones[zi], currency: code };
                          updated[index] = { ...updated[index], pricingZones: zones };
                          updateField('addons', updated);
                        }}
                      />
                    </View>
                  </View>
                </View>
              ))}
              <View style={styles.addonZoneButtons}>
                {(['country', 'city', 'radius'] as const).map(m => (
                  <TouchableOpacity
                    key={m}
                    style={styles.addAddonZoneBtn}
                    onPress={() => setAddonZonePicker({ addonIndex: index, zoneIndex: null, mode: m })}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name={m === 'country' ? 'earth-outline' : m === 'city' ? 'business-outline' : 'radio-button-on-outline'}
                      size={14}
                      color={m === 'country' ? colors.success : m === 'city' ? colors.primary : colors.warning}
                    />
                    <Text style={[styles.addAddonZoneText, { color: m === 'country' ? colors.success : m === 'city' ? colors.primary : colors.warning }]}>
                      {m === 'country' ? 'Country' : m === 'city' ? 'City' : 'Radius'}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
        ))}
        <TouchableOpacity
          style={styles.addAddonBtn}
          onPress={() => {
            const newAddon: ProductAddon = {
              id: `addon-${Date.now()}`,
              name: '',
              price: 0,
              isRequired: false,
            };
            updateField('addons', [...(formData.addons || []), newAddon]);
          }}
          activeOpacity={0.7}
        >
          <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
          <Text style={styles.addAddonText}>Add an add-on</Text>
        </TouchableOpacity>
      </View>

      {/* ── Location-based Pricing ───────────────────────────────────────── */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Location Pricing</Text>
          <Text style={styles.optionalLabel}>(Optional)</Text>
        </View>
        <Text style={styles.sectionDescription}>
          Set different prices for specific cities or geographic zones. When a customer's location matches a zone, that zone's price and currency apply instead of the base price.
        </Text>

        {(formData.pricingZones || []).map((zone, index) => (
          <View key={index} style={styles.zoneCard}>
            <View style={styles.zoneCardHeader}>
              <Text style={styles.zoneCardName}>{zone.name}</Text>
              <View style={styles.zoneCardActions}>
                <TouchableOpacity
                  onPress={() => { setEditingZoneIndex(index); setShowZonePicker(true); }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  style={{ marginRight: spacing.sm }}
                >
                  <Ionicons name="pencil-outline" size={18} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => updateField('pricingZones', (formData.pricingZones || []).filter((_, i) => i !== index))}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="close-circle" size={20} color={colors.error} />
                </TouchableOpacity>
              </View>
            </View>
            <Text style={styles.zoneCardMeta}>
              {zone.cities?.length ? `Cities: ${zone.cities.join(', ')}` : zone.radiusKm ? `Radius: ${zone.radiusKm} km` : 'Polygon zone'}
            </Text>
            <View style={styles.row}>
              <View style={styles.thirdWidth}>
                <Input
                  label="Price *"
                  value={zone.price.toString()}
                  onChangeText={(t) => {
                    const n = parseFloat(t);
                    const updated = [...(formData.pricingZones || [])];
                    updated[index] = { ...updated[index], price: isNaN(n) ? 0 : n };
                    updateField('pricingZones', updated);
                  }}
                  placeholder="0.00"
                  keyboardType="decimal-pad"
                />
              </View>
              <View style={styles.thirdWidth}>
                <Input
                  label="Sale Price"
                  value={zone.salePrice?.toString() || ''}
                  onChangeText={(t) => {
                    const n = parseFloat(t);
                    const updated = [...(formData.pricingZones || [])];
                    updated[index] = { ...updated[index], salePrice: t ? (isNaN(n) ? undefined : n) : undefined };
                    updateField('pricingZones', updated);
                  }}
                  placeholder="0.00"
                  keyboardType="decimal-pad"
                />
              </View>
              <View style={styles.thirdWidth}>
                <CurrencyPicker
                  label="Currency"
                  value={zone.currency || formData.currency}
                  onChange={(code) => {
                    const updated = [...(formData.pricingZones || [])];
                    updated[index] = { ...updated[index], currency: code };
                    updateField('pricingZones', updated);
                  }}
                />
              </View>
            </View>
          </View>
        ))}

        <TouchableOpacity
          style={styles.addAddonBtn}
          onPress={() => { setEditingZoneIndex(null); setShowZonePicker(true); }}
          activeOpacity={0.7}
        >
          <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
          <Text style={styles.addAddonText}>Add a pricing zone</Text>
        </TouchableOpacity>
      </View>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <View style={styles.actions}>
        {onCancel && (
          <Button title="Cancel" onPress={onCancel} variant="outline" style={styles.cancelButton} disabled={loading} />
        )}
        <Button title={submitLabel} onPress={handleSubmit} variant="primary" loading={loading} style={styles.submitButton} disabled={loading} />
      </View>
    </ScrollView>

    <BarcodeScanner
      visible={showBarcodeScanner}
      onClose={() => setShowBarcodeScanner(false)}
      onScanned={(barcode) => { updateField('barcode', barcode); setShowBarcodeScanner(false); }}
      title="Scan Product Barcode"
    />

    <ZoneMapPicker
      visible={showZonePicker}
      onClose={() => { setShowZonePicker(false); setEditingZoneIndex(null); }}
      initialData={editingZoneIndex !== null ? (() => {
        const z = (formData.pricingZones || [])[editingZoneIndex];
        if (!z) return undefined;
        if (z.mode === 'radius' || (!z.cities?.length && !z.mode)) {
          return { mode: 'radius' as const, name: z.name, price: z.price, center: z.center, radiusKm: z.radiusKm };
        }
        const zoneMode = (z.mode === 'country' ? 'country' : 'city') as 'country' | 'city';
        return { mode: zoneMode, name: z.name, price: z.price, cityName: z.cities?.[0], center: z.center };
      })() : undefined}
      onConfirm={(result: ZoneResult) => {
        const geo: Partial<ProductPricingZone> =
          (result.mode === 'city' || result.mode === 'country')
            ? {
                cities: [result.cityName],
                center: result.center,
                polygon: (result as any).polygon,
                polygons: (result as any).polygons,
                radiusKm: (result as any).radiusKm,
              }
            : {
                center: result.center,
                radiusKm: result.radiusKm,
              };

        const newZone: ProductPricingZone = {
          name: result.name,
          price: result.price,
          mode: result.mode,
          ...geo,
        };

        const zones = [...(formData.pricingZones || [])];
        if (editingZoneIndex !== null) {
          // Preserve salePrice/currency from existing zone
          newZone.salePrice = zones[editingZoneIndex].salePrice;
          newZone.currency = zones[editingZoneIndex].currency;
          zones[editingZoneIndex] = newZone;
        } else {
          zones.push(newZone);
        }
        updateField('pricingZones', zones);
        setShowZonePicker(false);
        setEditingZoneIndex(null);
      }}
    />

    {/* Addon zone picker */}
    <ZoneMapPicker
      visible={addonZonePicker !== null}
      onClose={() => setAddonZonePicker(null)}
      initialMode={addonZonePicker?.mode}
      initialData={addonZonePicker?.zoneIndex !== null && addonZonePicker !== null ? (() => {
        const z = ((formData.addons || [])[addonZonePicker.addonIndex]?.pricingZones || [])[addonZonePicker.zoneIndex!];
        if (!z) return undefined;
        if (z.mode === 'radius' || (!z.cities?.length && !z.mode)) {
          return { mode: 'radius' as const, name: z.name, price: z.price, center: z.center, radiusKm: z.radiusKm };
        }
        const zoneMode = (z.mode === 'country' ? 'country' : 'city') as 'country' | 'city';
        return { mode: zoneMode, name: z.name, price: z.price, cityName: z.cities?.[0], center: z.center };
      })() : undefined}
      onConfirm={(result: ZoneResult) => {
        if (!addonZonePicker) return;
        const { addonIndex, zoneIndex } = addonZonePicker;
        const geo: Partial<AddonPricingZone> =
          (result.mode === 'city' || result.mode === 'country')
            ? {
                cities: [result.cityName],
                center: result.center,
                polygon: (result as any).polygon,
                polygons: (result as any).polygons,
                radiusKm: (result as any).radiusKm,
              }
            : { center: result.center, radiusKm: result.radiusKm };

        const newZone: AddonPricingZone = { name: result.name, price: result.price, mode: result.mode, ...geo };

        const updated = [...(formData.addons || [])];
        const zones = [...(updated[addonIndex].pricingZones || [])];
        if (zoneIndex !== null) {
          zones[zoneIndex] = newZone;
        } else {
          zones.push(newZone);
        }
        updated[addonIndex] = { ...updated[addonIndex], pricingZones: zones };
        updateField('addons', updated);
        setAddonZonePicker(null);
      }}
    />
    </>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1 },
  section: {
    marginBottom: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.semibold,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
  },
  fieldHelper: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: spacing.xs,
  },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.xs, gap: spacing.xs },
  optionalLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    fontStyle: 'italic',
  },
  sectionDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.regular,
  },
  // ── Type chips ─────────────────────────────────────────────────────────────
  typeRow: { flexDirection: 'row', gap: spacing.sm },
  typeChip: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1.5,
    borderColor: withAlpha(colors.primary, 0.31),
    backgroundColor: colors.background.surface,
  },
  typeChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  typeChipText: { flex: 1 },
  typeChipLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  typeChipLabelActive: { color: colors.white },
  typeChipDesc: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.textLight,
  },
  typeChipDescActive: { color: 'rgba(255,255,255,0.8)' },
  // ── Layout ─────────────────────────────────────────────────────────────────
  row: { flexDirection: 'row', gap: spacing.sm },
  halfWidth: { flex: 1 },
  thirdWidth: { flex: 1 },
  flex1: { flex: 1 },
  // ── Barcode ────────────────────────────────────────────────────────────────
  inputLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  barcodeRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  scanButton: {
    width: 44,
    height: 44,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  // ── Toggle ─────────────────────────────────────────────────────────────────
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  toggleLabel: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    flex: 1,
    paddingRight: spacing.sm,
  },
  // ── Select / chips ─────────────────────────────────────────────────────────
  selectRow: { marginBottom: spacing.md },
  selectLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    color: colors.textDark,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.medium,
  },
  selectButtons: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  chipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  chipText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  chipTextActive: { color: colors.white },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.primary,
  },
  // ── Actions ────────────────────────────────────────────────────────────────
  actions: { flexDirection: 'row', gap: spacing.md, padding: spacing.md, marginBottom: spacing.xl },
  cancelButton: { flex: 1 },
  submitButton: { flex: 1 },
  addonCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
    backgroundColor: colors.lightGray,
  },
  addonHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  addonIndex: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
    fontWeight: '600',
  },
  addonToggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.xs,
  },
  addonToggleLabel: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
  },
  addAddonBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.primary,
    borderRadius: designTokens.borderRadius.md,
    borderStyle: 'dashed',
    marginTop: spacing.xs,
  },
  addAddonText: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
  // ── Addon location pricing ──────────────────────────────────────────────────
  addonZonesSection: {
    marginTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: spacing.sm,
  },
  addonZonesLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textMuted,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
  },
  addonZoneCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    backgroundColor: colors.background.primary,
  },
  addonZoneCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  addonZoneName: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  addonZoneMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.textMuted,
  },
  addonZoneButtons: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  addAddonZoneBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderStyle: 'dashed',
  },
  addAddonZoneText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  // ── Location pricing zone card ──────────────────────────────────────────────
  zoneCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    backgroundColor: colors.background.primary,
  },
  zoneCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  zoneCardName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    flex: 1,
  },
  zoneCardMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginBottom: spacing.sm,
  },
  zoneCardActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
